/**
 * Server Notices detection utilities
 * Centralized logic for identifying server notices users and rooms
 */
import logger from './logger';

/**
 * Check if a user ID is a server notices bot
 * @param {string} userId - Matrix user ID
 * @returns {boolean} - True if this is a server notices user
 */
export function isServerNoticesUser(userId) {
  if (!userId || typeof userId !== 'string') return false;
  
  // Match common server notices user patterns
  return !!(
    userId.match(/^@server:/i) || 
    userId.match(/^@notices:/i) ||
    userId.match(/^@servernotices:/i) ||
    userId.match(/^@server[-_]?notice/i)
  );
}

/**
 * Get the custom SVG avatar path for server notices
 * @returns {string} - Path to server notices SVG avatar
 */
export function getServerNoticesAvatar() {
  return '/images/svg/server-notices-avatar.svg';
}

/**
 * Check if a room is a server notices room
 * Uses the same logic as roomUtils.js isServerNoticesRoom
 * @param {Object} room - Matrix Room object
 * @param {Object} client - Matrix client instance
 * @returns {boolean} - True if this is a server notices room
 */
export function isServerNoticesRoom(room, client) {
  if (!room || !client) return false;

  try {
    // Method 1: Check room account data for m.server_notice event
    const serverNoticeEvent = room.getAccountData('m.server_notice');
    if (serverNoticeEvent) return true;

    // Method 2: Check room tags for m.server_notice tag
    const tagsEvent = room.getAccountData('m.tag');
    if (tagsEvent?.getContent()?.tags?.['m.server_notice']) return true;

    // Method 3: Check if room has the server notices user as a member
    // Use getMembers() instead of getJoinedMembers() to include invited members
    // This is critical for detecting server notices during initial invite state
    const members = room.getMembers();
    if (members && members.length === 2) {
      const hasServerNoticesUser = members.some(member => (
        isServerNoticesUser(member.userId)
      ));
      if (hasServerNoticesUser) return true;
    }

    return false;
  } catch (error) {
    logger.warn('[serverNoticesUtils] Error checking if room is server notices:', error);
    return false;
  }
}
